import type { FormEvent, ReactNode } from 'react'
import type { Appointment, AppointmentInput, AppointmentStatus } from '@/types/clinic'

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { CircleAlert, OctagonX, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/lib/clinic/store'
import { formatDate, formatTime } from '@/lib/dates'
import { isValidPhone, normalizePhone } from '@/lib/format'
import { findAppointmentConflicts, getFreeSlots, getScheduleWarnings } from '@/lib/clinic/selectors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import FormField from '@/components/app/form-field'
import SimpleSelect from '@/components/app/simple-select'
import PatientPicker from '@/components/app/patient-picker'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import { APPOINTMENT_DURATIONS, APPOINTMENT_STATUS_META } from '@/constants/clinic'

export interface AppointmentDialogOptions {
    appointmentId?: string
    patientId?: string
    date?: string
    time?: string
    treatmentId?: string
    reschedule?: boolean
}

interface AppointmentDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    options: AppointmentDialogOptions
    onSendConfirmation: (appointment: Appointment) => void
}

interface FormState {
    patientId: string
    phone: string
    date: string
    time: string
    treatmentId: string
    duration: number
    status: AppointmentStatus
    notes: string
    sendConfirmation: boolean
}

const AppointmentDialog = ({ open, onOpenChange, options, onSendConfirmation }: AppointmentDialogProps): ReactNode => {
    const { data, today, actions } = useClinic()
    const settings = data.settings.appointments
    const existing = options.appointmentId ? data.appointments.find(item => item.id === options.appointmentId) : undefined
    const isEdit = Boolean(existing)

    const [form, setForm] = useState<FormState>(() => {
        const patient = data.patients.find(item => item.id === (existing?.patientId ?? options.patientId))
        const treatment = data.treatments.find(item => item.id === (existing?.treatmentId ?? options.treatmentId))
        const reopened = options.reschedule && existing && (existing.status === 'cancelled' || existing.status === 'no-show')
        return {
            patientId: existing?.patientId ?? options.patientId ?? '',
            phone: existing?.phone ?? patient?.phone ?? '',
            date: existing?.date ?? options.date ?? today,
            time: existing?.time ?? options.time ?? '',
            treatmentId: existing?.treatmentId ?? options.treatmentId ?? '',
            duration: existing?.duration ?? treatment?.duration ?? settings.slotDuration,
            status: reopened ? 'scheduled' : existing?.status ?? 'scheduled',
            notes: existing?.notes ?? '',
            sendConfirmation: !existing || Boolean(options.reschedule)
        }
    })
    const [submitted, setSubmitted] = useState(false)

    const update = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm(current => ({ ...current, [key]: value }))

    const patient = data.patients.find(item => item.id === form.patientId)
    const treatmentOptions = data.treatments
        .filter(item => item.active || item.id === form.treatmentId)
        .map(item => ({ value: item.id, label: item.name }))
    const statusOptions = settings.enabledStatuses
        .concat(settings.enabledStatuses.includes(form.status) ? [] : [form.status])
        .map(status => ({ value: status, label: APPOINTMENT_STATUS_META[status].label }))

    const durationOptions = Array.from(new Set([...APPOINTMENT_DURATIONS, form.duration]))
        .sort((a, b) => a - b)
        .map(minutes => ({ value: String(minutes), label: `${minutes} minutes` }))

    const freeSlots = useMemo(
        () => (form.date ? getFreeSlots(settings, data.appointments, form.date, form.duration, existing?.id) : []),
        [settings, data.appointments, form.date, form.duration, existing?.id]
    )

    const conflicts = findAppointmentConflicts(data.appointments, {
        id: existing?.id,
        patientId: form.patientId,
        date: form.date,
        time: form.time,
        duration: form.duration
    })
    const patientName = (id: string) => data.patients.find(item => item.id === id)?.name ?? 'Another patient'

    const warnings: string[] = [
        ...(form.date && form.date < today && !isEdit ? ['This date is in the past.'] : []),
        ...getScheduleWarnings(settings, form.date, form.time, form.duration),
        ...conflicts.overlapping.map(item => `Overlaps with ${patientName(item.patientId)} at ${formatTime(item.time)} (${item.treatmentName || 'appointment'}).`),
        ...conflicts.samePatientSameDay.map(item => `${patient?.name ?? 'This patient'} already has an appointment at ${formatTime(item.time)} on this day.`)
    ]

    const errors = {
        patientId: !form.patientId ? 'Select a patient.' : '',
        phone: !isValidPhone(form.phone) ? 'Enter a valid 10 digit mobile number.' : '',
        date: !form.date ? 'Choose a date.' : '',
        time: !form.time ? 'Choose a time.' : '',
        treatmentId: !form.treatmentId ? 'Choose a treatment.' : ''
    }
    const hasErrors = Object.values(errors).some(Boolean)

    const handlePatientChange = (patientId: string) => {
        const next = data.patients.find(item => item.id === patientId)
        setForm(current => {
            const previous = data.patients.find(item => item.id === current.patientId)
            const keepPhone = current.phone && current.phone !== previous?.phone
            return { ...current, patientId, phone: keepPhone ? current.phone : next?.phone ?? '' }
        })
    }

    const handleTreatmentChange = (treatmentId: string) => {
        const treatment = data.treatments.find(item => item.id === treatmentId)
        setForm(current => ({ ...current, treatmentId, duration: treatment?.duration ?? current.duration }))
    }

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault()
        setSubmitted(true)
        if (hasErrors) {
            toast.error('Please complete the highlighted fields')
            return
        }
        if (conflicts.duplicate) {
            toast.error('This patient is already booked at that time')
            return
        }

        const treatment = data.treatments.find(item => item.id === form.treatmentId)
        const payload: AppointmentInput = {
            patientId: form.patientId,
            phone: normalizePhone(form.phone),
            date: form.date,
            time: form.time,
            duration: form.duration,
            treatmentId: form.treatmentId || null,
            treatmentName: treatment?.name ?? existing?.treatmentName ?? '',
            notes: form.notes.trim(),
            status: form.status
        }
        const name = patient?.name ?? 'the patient'
        const when = `${formatDate(form.date)} at ${formatTime(form.time)}`

        if (existing) {
            actions.updateAppointment(existing.id, payload)
            toast.success(options.reschedule ? 'Appointment rescheduled' : 'Appointment updated', { description: `${name}, ${when}` })
            if (form.sendConfirmation) onSendConfirmation({ ...existing, ...payload })
        }
        else {
            const created = actions.addAppointment(payload)
            toast.success('Appointment booked', { description: `${name}, ${when}` })
            if (form.sendConfirmation) onSendConfirmation(created)
        }
        onOpenChange(false)
    }

    const title = options.reschedule ? 'Reschedule appointment' : isEdit ? 'Edit appointment' : 'Book appointment'

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl'>
                <form onSubmit={handleSubmit} noValidate className='grid gap-5'>
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>
                            {options.reschedule ? 'Pick a new date and time. The patient can be notified on WhatsApp.' : 'Choose the patient, time and treatment for this visit.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className='grid gap-4 sm:grid-cols-2'>
                        <FormField label='Patient' htmlFor='appointment-patient' required error={submitted ? errors.patientId : ''} className='sm:col-span-2'>
                            <PatientPicker
                                id='appointment-patient'
                                value={form.patientId}
                                onChange={handlePatientChange}
                                invalid={submitted && Boolean(errors.patientId)}
                            />
                        </FormField>

                        {patient?.allergies && (
                            <p className='flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 sm:col-span-2'>
                                <CircleAlert className='size-4 shrink-0' aria-hidden='true' />
                                Allergies on record: {patient.allergies}
                            </p>
                        )}

                        <FormField label='Phone number' htmlFor='appointment-phone' required error={submitted ? errors.phone : ''}>
                            <Input
                                id='appointment-phone'
                                inputMode='tel'
                                value={form.phone}
                                onChange={event => update('phone', event.target.value)}
                                placeholder='98470 12345'
                                aria-invalid={submitted && Boolean(errors.phone)}
                            />
                        </FormField>

                        <FormField label='Treatment' htmlFor='appointment-treatment' required error={submitted ? errors.treatmentId : ''}>
                            <SimpleSelect
                                id='appointment-treatment'
                                value={form.treatmentId}
                                onValueChange={handleTreatmentChange}
                                options={treatmentOptions}
                                placeholder='Select treatment'
                                invalid={submitted && Boolean(errors.treatmentId)}
                            />
                        </FormField>

                        <FormField label='Date' htmlFor='appointment-date' required error={submitted ? errors.date : ''}>
                            <Input
                                id='appointment-date'
                                type='date'
                                value={form.date}
                                onChange={event => update('date', event.target.value)}
                                aria-invalid={submitted && Boolean(errors.date)}
                            />
                        </FormField>

                        <div className='grid grid-cols-2 gap-3'>
                            <FormField label='Time' htmlFor='appointment-time' required error={submitted ? errors.time : ''}>
                                <Input
                                    id='appointment-time'
                                    type='time'
                                    step={300}
                                    value={form.time}
                                    onChange={event => update('time', event.target.value)}
                                    aria-invalid={submitted && Boolean(errors.time)}
                                />
                            </FormField>

                            <FormField label='Duration' htmlFor='appointment-duration'>
                                <SimpleSelect
                                    id='appointment-duration'
                                    value={String(form.duration)}
                                    onValueChange={value => update('duration', Number(value) || settings.slotDuration)}
                                    options={durationOptions}
                                />
                            </FormField>
                        </div>

                        {form.date && (
                            <div className='sm:col-span-2'>
                                <p className='mb-2 text-xs font-medium text-muted-foreground'>
                                    {freeSlots.length ? `Free slots on ${formatDate(form.date)}` : `No free slots within clinic hours on ${formatDate(form.date)}`}
                                </p>
                                {freeSlots.length > 0 && (
                                    <div className='flex flex-wrap gap-1.5'>
                                        {freeSlots.slice(0, 14).map(slot => (
                                            <button
                                                key={slot}
                                                type='button'
                                                onClick={() => update('time', slot)}
                                                aria-pressed={form.time === slot}
                                                className={cn(
                                                    'h-8 rounded-md border px-2.5 text-xs font-medium transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                                                    form.time === slot
                                                        ? 'border-primary bg-primary text-primary-foreground'
                                                        : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-accent'
                                                )}
                                            >
                                                {formatTime(slot)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <FormField label='Status' htmlFor='appointment-status'>
                            <SimpleSelect
                                id='appointment-status'
                                value={form.status}
                                onValueChange={value => update('status', value as AppointmentStatus)}
                                options={statusOptions}
                            />
                        </FormField>

                        <FormField label='Notes' htmlFor='appointment-notes' className='sm:col-span-2'>
                            <Textarea
                                id='appointment-notes'
                                value={form.notes}
                                onChange={event => update('notes', event.target.value)}
                                placeholder='Reason for visit or anything the doctor should know'
                                rows={2}
                            />
                        </FormField>
                    </div>

                    {conflicts.duplicate && (
                        <div role='alert' className='flex gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800'>
                            <OctagonX className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                            <p>
                                <span className='font-semibold'>Duplicate booking.</span> {patient?.name} is already booked at {formatTime(conflicts.duplicate.time)} on this day for {conflicts.duplicate.treatmentName || 'an appointment'}. Choose another time or edit that booking.
                            </p>
                        </div>
                    )}

                    {!conflicts.duplicate && warnings.length > 0 && (
                        <div role='status' className='flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
                            <TriangleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                            <div>
                                <p className='font-semibold'>Please check before saving</p>
                                <ul className='mt-1 list-disc space-y-0.5 pl-4'>
                                    {warnings.map(warning => <li key={warning}>{warning}</li>)}
                                </ul>
                            </div>
                        </div>
                    )}

                    <div className='flex items-center gap-2'>
                        <Checkbox
                            id='appointment-whatsapp'
                            checked={form.sendConfirmation}
                            onCheckedChange={checked => update('sendConfirmation', checked === true)}
                        />
                        <Label htmlFor='appointment-whatsapp' className='text-sm font-normal'>
                            {isEdit ? 'Send the updated details on WhatsApp after saving' : 'Send a WhatsApp confirmation after booking'}
                        </Label>
                    </div>

                    <DialogFooter>
                        <DialogClose render={<Button type='button' variant='outline' />}>
                            Cancel
                        </DialogClose>
                        <Button type='submit' disabled={Boolean(conflicts.duplicate)}>
                            {warnings.length > 0 && !conflicts.duplicate
                                ? (isEdit ? 'Save anyway' : 'Book anyway')
                                : (isEdit ? 'Save changes' : 'Book appointment')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default AppointmentDialog
